import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@jeraisonne/ui';
import { useAuth } from '@/lib/auth';
import { useAuthoredThemes } from '@/lib/themes/themes-queries';
import { supabase } from '@/lib/supabase';
import { SCHOOL_LEVELS } from '@jeraisonne/db';
import type { SchoolLevel } from '@jeraisonne/db';
import { IllustrationEditor } from '@/components/illustration-editor';

export function TutorThemeEditPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data: themes = [] } = useAuthoredThemes(user?.id);
  const theme = themes.find((t) => t.id === id);

  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [shortDescription, setShortDescription] = useState('');
  const [descriptionMd, setDescriptionMd] = useState('');
  const [minLevel, setMinLevel] = useState<SchoolLevel | ''>('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (theme) {
      setTitle(theme.title);
      setSlug(theme.slug);
      setShortDescription(theme.short_description);
      setDescriptionMd(theme.description_md);
      setMinLevel((theme.min_level as SchoolLevel | null) ?? '');
    }
  }, [theme]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSaving(true);
    const { error: updateErr } = await supabase
      .from('themes')
      .update({
        title,
        slug,
        short_description: shortDescription,
        description_md: descriptionMd,
        min_level: minLevel || null,
      })
      .eq('id', id);
    setSaving(false);
    if (updateErr) {
      setError(updateErr.message);
      return;
    }
    navigate({ to: '/tutor/themes' });
  }

  if (!theme) {
    return (
      <div className="mx-auto w-full max-w-2xl px-4 py-6">
        <p className="text-ink-500 text-sm">Thème introuvable.</p>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-2xl px-4 py-6 sm:py-8">
      <Link
        to="/tutor/themes"
        className="text-primary-600 inline-flex min-h-[2.75rem] items-center gap-1 text-sm font-semibold"
      >
        <ArrowLeft className="h-4 w-4" /> Retour
      </Link>

      <h1 className="font-display text-ink-900 mt-3 text-[24px] font-bold">Éditer le thème</h1>

      <form onSubmit={onSubmit} className="mt-6 flex flex-col gap-4">
        <Field label="Titre" required>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            maxLength={120}
            className="border-ink-200 focus:border-primary-500 min-h-[2.75rem] w-full rounded-xl border bg-white px-4 py-2 text-sm outline-none"
          />
        </Field>

        <Field label="Slug" hint="URL publique : /themes/<slug>">
          <input
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            pattern="^[a-z0-9]+(-[a-z0-9]+)*$"
            minLength={2}
            maxLength={80}
            className="border-ink-200 focus:border-primary-500 min-h-[2.75rem] w-full rounded-xl border bg-white px-4 py-2 font-mono text-sm outline-none"
          />
        </Field>

        <Field label="Description courte">
          <textarea
            value={shortDescription}
            onChange={(e) => setShortDescription(e.target.value)}
            maxLength={300}
            rows={2}
            className="border-ink-200 focus:border-primary-500 w-full rounded-xl border bg-white px-4 py-2 text-sm outline-none"
          />
        </Field>

        <Field label="Description longue (markdown)">
          <textarea
            value={descriptionMd}
            onChange={(e) => setDescriptionMd(e.target.value)}
            maxLength={20000}
            rows={8}
            className="border-ink-200 focus:border-primary-500 w-full rounded-xl border bg-white px-4 py-2 font-mono text-xs outline-none"
          />
        </Field>

        <Field label="Niveau minimum">
          <select
            value={minLevel}
            onChange={(e) => setMinLevel(e.target.value as SchoolLevel | '')}
            className="border-ink-200 focus:border-primary-500 min-h-[2.75rem] w-full rounded-xl border bg-white px-4 py-2 text-sm outline-none"
          >
            <option value="">Tous niveaux</option>
            {SCHOOL_LEVELS.map((l) => (
              <option key={l.value} value={l.value}>
                {l.label}
              </option>
            ))}
          </select>
        </Field>

        {error && <p className="text-accent-600 text-sm">{error}</p>}

        <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
          <Link
            to="/tutor/themes"
            className="text-ink-700 border-ink-200 inline-flex min-h-[2.75rem] items-center justify-center rounded-xl border px-4 text-sm font-semibold"
          >
            Annuler
          </Link>
          <Button type="submit" disabled={saving} className="min-h-[2.75rem] w-full sm:w-auto">
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </div>
      </form>

      <div className="mt-6">
        <IllustrationEditor
          entityType="theme"
          entityId={id}
          initialUrl={theme.illustration_url}
          initialPrompt={theme.illustration_prompt}
          title={theme.title}
          level={theme.min_level ?? undefined}
        />
      </div>
    </div>
  );
}

function Field({
  label,
  hint,
  required,
  children,
}: {
  label: string;
  hint?: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="font-display text-ink-800 text-[13px] font-semibold">
        {label}
        {required && <span className="text-accent-600 ml-0.5">*</span>}
      </span>
      {children}
      {hint && <span className="text-ink-400 text-[11px]">{hint}</span>}
    </label>
  );
}
